import { useQuery } from "@tanstack/react-query";
import { jwtDecode } from "jwt-decode";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "@hubertai/hubert-component-library";
import { fetchInvitationQuery } from "../../../behavior/api/invitation";
import {
    clearTokenFromStorage,
    getAccessToken,
    getCurrentUser,
    isTokenExpiring,
} from "../../../behavior/security/auth.service";
import Header from "../../../components/header/Header";
import { PageRoute, PageState } from "../../../constants";
import type { Invitation, InvitationTokenPayload } from "../../../types/invitation";
import InviteEmailSignIn from "./InviteEmailSignIn";
import "./LoginInviteEmail.css";

const LoginInviteEmail = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const token = searchParams.get("token");
    const jwt = token ? jwtDecode<InvitationTokenPayload>(token) : null;

    const { data: invitation, isLoading: loading } = useQuery<Invitation>({
        queryKey: ["invitation", token],
        queryFn: fetchInvitationQuery(token),
        enabled: !!token,
    });

    const redirectToInterview = useCallback(
        (accessToken: string, displayName: string) => {
            if (token && jwt && invitation) {
                const urlSearchParams = new URLSearchParams({
                    interviewId: invitation.publicInterviewId ?? invitation.interviewId,
                    token: accessToken,
                    displayName: displayName,
                });
                navigate(`${PageRoute.PRE_INTERVIEW}?${urlSearchParams}`, {
                    replace: true,
                });
            }
        },
        [navigate, token, jwt, invitation],
    );

    useEffect(() => {
        if (token && jwt) {
            const user = getCurrentUser();
            if (user) {
                if (user.email !== jwt.email) clearTokenFromStorage();
            }
        } else {
            toast.error(t("AppMessages.invalid-invitation"));
            navigate("/", { replace: true });
        }

        if (!loading && invitation) {
            const accessToken = getAccessToken();
            if (accessToken) {
                if (isTokenExpiring(accessToken)) {
                    const currentUrl = `${PageRoute.PRE_INTERVIEW_LOGIN}?token=${token}`;
                    navigate(PageRoute.TRANSITION_PAGE, {
                        state: { redirectUrl: currentUrl },
                    });
                } else {
                    const type = invitation.__typename ?? invitation.type;
                    if (type === "Invitation") redirectToInterview(accessToken, invitation.invitee.displayName);
                    else navigate(`${PageRoute.INVITATION_VERIFICATION}?token=${token}`, { replace: true });
                }
            }
        }
    }, [invitation, loading, navigate, redirectToInterview, t, token, jwt]);

    const skipToPreInterview = useCallback(() => {
        const type = invitation?.__typename ?? invitation?.type;
        if (type === "Invitation" && token && jwt && invitation) {
            const urlSearchParams = new URLSearchParams({
                interviewId: invitation.publicInterviewId ?? invitation.interviewId,
                redirectUrl: `${PageRoute.POST_INTERVIEW_LOGIN}?state=${PageState.INVITATION_POST_INTERVIEW_SIGN_IN}&token=${token}`,
                displayName: invitation.invitee.displayName,
            });
            navigate(`${PageRoute.PRE_INTERVIEW}?${urlSearchParams}`);
        } else {
            toast.info("Invalid Invitation.");
        }
    }, [invitation, navigate, token, jwt]);

    return (
        <>
            <Header type="SignInInviteEmail" interviewRedirect={skipToPreInterview} />
            <InviteEmailSignIn
                invitation={invitation}
                state={PageState.INVITATION_SIGN_IN}
                postLoginEvent={redirectToInterview}
                email={jwt?.email}
            />
        </>
    );
};

export default LoginInviteEmail;
